/**
 * Toolbar: transport controls, time multiplier, display mode, reset semantics.
 *
 * The two "reset" actions are deliberately separate and separately labelled,
 * because conflating them is the easiest way to lose a user's work:
 *
 *   Reset      — restore the terrain exactly as it was generated from this seed
 *   Regenerate — build a *new* terrain from the current parameters and seed
 */

import {
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Shuffle,
  StepForward,
  Grid3x3,
  Droplets,
  Sun,
  Save,
  Upload,
  Download,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

import { TIME_SCALES } from '../simulation/constants.ts';
import { VIEW_MODES, type ViewMode } from '../rendering/viewModes.ts';
import type { SimulationRuntime } from '../app/runtime.ts';
import { useRuntimeVersion } from '../app/runtime.ts';

interface ToolbarProps {
  runtime: SimulationRuntime;
  brushActive: boolean;
  onToggleBrush: () => void;
  onExportPng: () => void;
  onImportPng: () => void;
  onExportBinary: () => void;
  onSaveWorld: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

function IconButton({
  title,
  onClick,
  active,
  disabled,
  children,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-7 items-center gap-1.5 border px-2 text-[11px] transition-colors ${
        active
          ? 'border-ink bg-ink text-paper-raised'
          : 'border-rule bg-paper-raised text-ink hover:border-rule-strong'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

export function Toolbar({
  runtime,
  brushActive,
  onToggleBrush,
  onExportPng,
  onImportPng,
  onExportBinary,
  onSaveWorld,
  collapsed,
  onToggleCollapsed,
}: ToolbarProps) {
  useRuntimeVersion(runtime);
  const params = runtime.getParams();

  if (collapsed) {
    return (
      <header className="flex h-9 flex-none items-center justify-between border-b border-rule bg-paper-raised px-3">
        <h1 className="text-[13px] tracking-tight text-ink">山地实验室</h1>
        <button
          type="button"
          aria-label="展开顶部工具栏"
          aria-expanded={false}
          onClick={onToggleCollapsed}
          className="inline-flex h-7 items-center gap-1 border border-rule px-2 text-[11px] text-ink-muted"
        >
          <ChevronDown size={12} />
          工具
        </button>
      </header>
    );
  }

  return (
    <header className="flex max-h-[55vh] flex-none flex-wrap items-center gap-x-3 gap-y-1.5 overflow-y-auto border-b border-rule bg-paper-raised px-3 py-2 md:max-h-none md:overflow-visible">
      <div className="flex items-center gap-1.5">
        <h1 className="mr-2 text-[13px] tracking-tight text-ink">山地实验室</h1>

        <IconButton
          title={runtime.running ? '暂停模拟' : '开始运行模拟'}
          onClick={() => runtime.toggleRunning()}
          active={runtime.running}
        >
          {runtime.running ? <Pause size={12} /> : <Play size={12} />}
          {runtime.running ? '暂停' : '运行'}
        </IconButton>
        <IconButton title="暂停时推进一个 tick" onClick={() => runtime.step()}>
          <StepForward size={12} />
          步进
        </IconButton>
        <IconButton title="恢复到当前种子刚生成时的地形" onClick={() => runtime.resetSimulation()}>
          <RotateCcw size={12} />
          重置
        </IconButton>
        <IconButton title="按当前参数重新生成地形，种子不变" onClick={() => runtime.regenerate()}>
          <RefreshCw size={12} />
          重新生成
        </IconButton>
        <IconButton
          title="换一个随机种子，生成全新地形"
          onClick={() => runtime.setSeed(Math.floor(Math.random() * 99999))}
        >
          <Shuffle size={12} />
          换随机种子
        </IconButton>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-ink-faint">倍速</span>
        <div className="flex border border-rule">
          {TIME_SCALES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => runtime.mutate((p) => void (p.engine.timeScale = s))}
              className={`h-6 min-w-[36px] px-1.5 font-mono text-[10px] tabular-nums transition-colors ${
                params.engine.timeScale === s
                  ? 'bg-ink text-paper-raised'
                  : 'bg-paper-raised text-ink-muted hover:bg-paper-sunken'
              }`}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-ink-faint">显示</span>
        <select
          value={runtime.mode}
          onChange={(e) => {
            runtime.mode = e.target.value as ViewMode;
            runtime.touch();
          }}
          className="h-6 appearance-none border border-rule bg-paper-raised px-1.5 text-[11px] text-ink outline-none focus:border-rule-strong"
        >
          {VIEW_MODES.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <IconButton
          title={`水层显示：${runtime.showWater ? '开' : '关'}`}
          onClick={() => {
            runtime.showWater = !runtime.showWater;
            runtime.touch();
          }}
          active={runtime.showWater}
        >
          <Droplets size={12} />
        </IconButton>
        <IconButton
          title={`网格叠加：${runtime.gridStep > 0 ? `每 ${runtime.gridStep} 格` : '关'}`}
          onClick={() => {
            runtime.gridStep = runtime.gridStep === 0 ? 16 : runtime.gridStep === 16 ? 64 : 0;
            runtime.touch();
          }}
          active={runtime.gridStep > 0}
        >
          <Grid3x3 size={12} />
        </IconButton>
        <IconButton
          title={`亮度 ${Math.round(runtime.exposure * 100)}%`}
          onClick={() => {
            runtime.exposure = runtime.exposure > 1.15 ? 0.85 : runtime.exposure + 0.15;
            runtime.touch();
          }}
        >
          <Sun size={12} />
        </IconButton>

        <span className="mx-1 h-4 w-px bg-rule" />

        <IconButton title="用笔刷直接修改地形" onClick={onToggleBrush} active={brushActive}>
          笔刷
        </IconButton>
        <IconButton title="导出高程图为 16 位 PNG" onClick={onExportPng}>
          <Download size={12} />
        </IconButton>
        <IconButton title="从 PNG 导入高程图" onClick={onImportPng}>
          <Upload size={12} />
        </IconButton>
        <IconButton title="导出完整世界状态（.f32）" onClick={onExportBinary}>
          <Save size={12} />
        </IconButton>
        <IconButton title="保存整个世界（状态 + 参数）为 JSON" onClick={onSaveWorld}>
          <Save size={12} />
        </IconButton>
        <IconButton title="折叠顶部工具栏" onClick={onToggleCollapsed}>
          <ChevronUp size={12} />
        </IconButton>
      </div>
    </header>
  );
}
